import { useNavigate } from 'react-router-dom';
import { MapPin } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useArrivalInformation } from '../hooks/useArrivalInformation';

/**
 * Dashboard tile linking to Local Travel Options (/travel).
 *
 * Placement-gated: this tile was originally a static cardgrid entry rendered
 * from day 1, which was reported upstream as "the portal shows travel info
 * before placement". The endpoint-driven ArrivalInformationCard was always
 * gated on `available` — this static tile was the actual day-1 surface.
 * Pre-placement there is no destination to get around, so the tile now keys
 * off the same placement signal as its cardgrid neighbour.
 *
 * It shares useArrivalInformation() with ArrivalInformationCard (same
 * queryKey → one request per dashboard load, whichever mounts first) and the
 * same silent-absence contract: loading, error, or `available: false` render
 * NOTHING — no skeleton; see ArrivalInformationCard's comment for why.
 */
export function LocalTravelOptionsCard() {
  const navigate = useNavigate();
  const { data, isLoading, isError } = useQuery(useArrivalInformation());

  if (isLoading || isError || !data || !data.available) return null;

  return (
    <div className="card actioncard" data-testid="local-travel-options-card">
      <div className="actioncard-head">
        <span className="actioncard-icon" aria-hidden><MapPin size={20} /></span>
        <h2 style={{ margin: 0 }}>Local Travel Options</h2>
      </div>
      <p style={{ margin: 0, color: 'var(--ink-2)' }}>Getting around your destination</p>
      <button type="button" className="btn-outline" onClick={() => navigate('/travel')}>
        View Travel Options
      </button>
    </div>
  );
}
